import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ContentsAPI, TokenManager } from '../api/api';

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
    const [accessibleSections, setAccessibleSections] = useState([]);
    const [accessibleCategories, setAccessibleCategories] = useState([]);
    const [accessibleParts, setAccessibleParts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPermissions = useCallback(async () => {
        if (!TokenManager.isAuthenticated()) {
            setAccessibleSections([]);
            setAccessibleCategories([]);
            setAccessibleParts([]);
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const response = await ContentsAPI.getMenu();

            const sections = response.data?.sections || [];
            const categories = [];
            const parts = [];

            sections.forEach(section => {
                if (section.categories) {
                    section.categories.forEach(category => {
                        categories.push({
                            ...category,
                            sectionId: section.id,
                            sectionName: section.name
                        });
                        if (category.parts) {
                            category.parts.forEach(part => {
                                // 기본 권한 객체 (서버에서 안 올 경우 대비)
                                const defaultPermissions = {
                                    canView: true,
                                    canCreate: false,
                                    canEdit: false,
                                    canDelete: false,
                                    canUpload: false
                                };
                                parts.push({
                                    ...part,
                                    categoryId: category.id,
                                    categoryName: category.name,
                                    sectionId: section.id,
                                    sectionName: section.name,
                                    permissions: part.permissions || defaultPermissions
                                });
                            });
                        }
                    });
                }
            });

            setAccessibleSections(sections);
            setAccessibleCategories(categories);
            setAccessibleParts(parts);
        } catch (err) {
            console.error('Failed to fetch permissions:', err);
            setError(err.message);
            setAccessibleSections([]);
            setAccessibleCategories([]);
            setAccessibleParts([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPermissions();

        // 토큰 갱신 시 권한 재로드
        const handleTokenRefresh = () => {
            console.log('토큰 갱신됨 - 권한 재로드');
            fetchPermissions();
        };

        window.addEventListener('tokenRefreshed', handleTokenRefresh);

        return () => {
            window.removeEventListener('tokenRefreshed', handleTokenRefresh);
        };
    }, [fetchPermissions]);

    const canAccessSection = useCallback((sectionName) => {
        return accessibleSections.some(s => s.name === sectionName);
    }, [accessibleSections]);

    const canAccessCategory = useCallback((categoryName, sectionName = null) => {
        if (sectionName) {
            return accessibleCategories.some(
                c => c.name === categoryName && c.sectionName === sectionName
            );
        }
        return accessibleCategories.some(c => c.name === categoryName);
    }, [accessibleCategories]);

    const canAccessPart = useCallback((partName, categoryName = null) => {
        if (categoryName) {
            return accessibleParts.some(
                p => p.name === partName && p.categoryName === categoryName
            );
        }
        return accessibleParts.some(p => p.name === partName);
    }, [accessibleParts]);

    const getSectionByName = useCallback((sectionName) => {
        return accessibleSections.find(s => s.name === sectionName);
    }, [accessibleSections]);

    const getCategoriesBySection = useCallback((sectionName) => {
        return accessibleCategories.filter(c => c.sectionName === sectionName);
    }, [accessibleCategories]);

    const getPartsByCategory = useCallback((categoryName) => {
        return accessibleParts.filter(p => p.categoryName === categoryName);
    }, [accessibleParts]);

    // 파트별 상세 권한 확인 함수들
    const canCreateInPart = useCallback((partId) => {
        const part = accessibleParts.find(p => p.id === partId);
        return part?.permissions?.canCreate || false;
    }, [accessibleParts]);

    const canEditInPart = useCallback((partId) => {
        const part = accessibleParts.find(p => p.id === partId);
        return part?.permissions?.canEdit || false;
    }, [accessibleParts]);

    const canDeleteInPart = useCallback((partId) => {
        const part = accessibleParts.find(p => p.id === partId);
        return part?.permissions?.canDelete || false;
    }, [accessibleParts]);

    const canUploadInPart = useCallback((partId) => {
        const part = accessibleParts.find(p => p.id === partId);
        return part?.permissions?.canUpload || false;
    }, [accessibleParts]);

    const getPartPermissions = useCallback((partId) => {
        const part = accessibleParts.find(p => p.id === partId);
        return part?.permissions || null;
    }, [accessibleParts]);

    const value = {
        accessibleSections,
        accessibleCategories,
        accessibleParts,
        isLoading,
        error,
        canAccessSection,
        canAccessCategory,
        canAccessPart,
        getSectionByName,
        getCategoriesBySection,
        getPartsByCategory,
        refreshPermissions: fetchPermissions,
        // 파트별 상세 권한 함수
        canCreateInPart,
        canEditInPart,
        canDeleteInPart,
        canUploadInPart,
        getPartPermissions,
    };

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
}

export function usePermission() {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermission must be used within a PermissionProvider');
    }
    return context;
}

export default PermissionContext;
